import { useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import {
  FEATURE_CLIENT_REPORT_QUEUE,
  STATUS_COLOR,
  downloadReportFile,
  fetchQueue,
  fetchReport,
  holdReport,
  markSent,
  prepareReport,
  reportMailto,
  resumeReport,
  toErrorMessage,
  type QueueFilter,
  type ReportCell,
  type ReportDetail,
  type ReportQueue,
} from './clientReportsApi';

const ORIGIN_LABEL: Record<string, string> = {
  confirmed: 'potwierdzone',
  plan: 'plan',
  registry: 'z kartoteki',
};

/** Komórka podglądu: wartość i oznaczenie „plan” / „potwierdzone”; brak — na czerwono. */
export const CellView = ({ cell }: { cell: ReportCell | undefined }) => {
  if (!cell) return null;
  if (cell.origin === 'missing') {
    return (
      <Tooltip title={cell.source ?? ''}>
        <Chip size="small" color="error" label="brak" data-origin="missing" />
      </Tooltip>
    );
  }
  const tag = ORIGIN_LABEL[cell.origin];
  return (
    <Box component="span" data-origin={cell.origin}>
      {cell.value}{' '}
      {tag ? (
        <Tooltip title={cell.source ?? ''}>
          <Chip
            size="small"
            variant={cell.origin === 'confirmed' ? 'filled' : 'outlined'}
            color={cell.origin === 'confirmed' ? 'success' : 'default'}
            label={tag}
          />
        </Tooltip>
      ) : null}
    </Box>
  );
};

/** Okno „Wstrzymaj” z powodem. */
const HoldDialog = ({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) => {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Wstrzymaj raport</DialogTitle>
      <DialogContent>
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          label="Powód wstrzymania"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'client-report-hold-reason' } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Wróć</Button>
        <Button
          variant="contained"
          disabled={reason.trim() === ''}
          onClick={() => void onConfirm(reason.trim())}
          data-testid="client-report-hold-confirm"
        >
          Wstrzymaj
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Podgląd raportu (makieta p. Mariusza, ekran 8; BR-CS-01): wiersze z „plan”
 * i „potwierdzone”, braki, plik, szkic maila, „wysłane ręcznie”, wstrzymanie,
 * historia. Wysyła CS ze swojej poczty — system nic nie wysyła.
 */
export const ReportPreview = ({ id, onBack, onChanged }: { id: number; onBack: () => void; onChanged: () => void }) => {
  const notify = useNotify();
  const [report, setReport] = useState<ReportDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState('');
  const [withMissing, setWithMissing] = useState(false);
  const [holding, setHolding] = useState(false);
  const [busy, setBusy] = useState(false);

  const show = useCallback((r: ReportDetail) => {
    setReport(r);
    setRecipients(r.sentRecipients ?? r.recipients.join('; '));
    setWithMissing(false);
    setError(null);
  }, []);

  useEffect(() => {
    fetchReport(id)
      .then(show)
      .catch((e) => setError(toErrorMessage(e)));
  }, [id, show]);

  const act = async (fn: () => Promise<ReportDetail>, done: string) => {
    setBusy(true);
    try {
      show(await fn());
      notify(done, { type: 'success' });
      onChanged();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!report) return <Typography variant="body2">Wczytuję raport…</Typography>;

  const prepared = report.status !== 'to_prepare';
  const open = ['to_prepare', 'incomplete', 'to_accept'].includes(report.status);
  const missing = (report.missingCount ?? 0) > 0;
  const list = recipients
    .split(/[;,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const mail = report.draft ? reportMailto(list, report.draft.subject, report.draft.body) : null;

  const copy = async () => {
    if (!report.draft) return;
    try {
      await navigator.clipboard.writeText(`${report.draft.subject}\n\n${report.draft.body}`);
      notify('Skopiowano temat i treść wiadomości', { type: 'info' });
    } catch {
      notify('Nie udało się skopiować — zaznacz treść i skopiuj ręcznie', { type: 'warning' });
    }
  };

  return (
    <Box data-testid="client-report-preview">
      <Button size="small" onClick={onBack} sx={{ mb: 1 }}>
        ← Kolejka raportów
      </Button>
      <Card variant="outlined" sx={{ mb: 1 }}>
        <CardContent>
          <Typography variant="h6">
            {report.reportName} <Typography component="span" variant="body2" color="text.secondary">· {report.clientName}</Typography>
          </Typography>
          <Box
            sx={{ display: 'grid', gap: 1, mt: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))' }}
          >
            <Typography variant="body2">Termin: {dateTimeNoSeconds(report.dueAt)}</Typography>
            <Typography variant="body2">
              Okres: {report.periodLabel} ({report.dateBasisLabel})
            </Typography>
            <Typography variant="body2">Lokalizacje: {report.locationsLabel}</Typography>
            <Typography variant="body2" component="div" data-testid="client-report-status">
              Stan: <Chip size="small" color={STATUS_COLOR[report.status] ?? 'default'} label={report.statusLabel} />
              {report.overdue ? <Chip size="small" color="error" label="po terminie" sx={{ ml: 0.5 }} /> : null}
            </Typography>
            <Typography variant="body2">
              Przygotowany: {report.preparedAt ? dateTimeNoSeconds(report.preparedAt) : '—'}
              {report.preparedAt ? ` (${report.preparedByName ?? 'automat'})` : ''}
            </Typography>
            <Typography variant="body2">Adresaci: {report.recipients.length ? `${report.recipients.length} adres(y)` : 'nie wpisano'}</Typography>
          </Box>
          {report.status === 'on_hold' ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Wstrzymany {report.heldAt ? dateTimeNoSeconds(report.heldAt) : ''} przez {report.heldByName ?? '—'}: {report.holdReason}
            </Alert>
          ) : null}
          {report.status === 'sent' ? (
            <Alert severity="success" sx={{ mt: 1 }} data-testid="client-report-sent-info">
              Wysłany ręcznie {report.sentAt ? dateTimeNoSeconds(report.sentAt) : ''} przez {report.sentByName ?? '—'}
              {report.sentRecipients ? ` do: ${report.sentRecipients}` : ''}
              {report.sentWithMissing ? ' — z oznaczonymi brakami' : ''}
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {prepared ? (
        <Card variant="outlined" sx={{ mb: 1 }}>
          <CardContent>
            {report.rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                W okresie raportu nie ma zleceń tego klienta.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" data-testid="client-report-rows">
                  <TableHead>
                    <TableRow>
                      {report.headers.map((h) => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.rows.map((r) => (
                      <TableRow
                        key={r.userOfferId}
                        data-testid={`client-report-row-${r.userOfferId}`}
                        sx={r.missing.length ? { bgcolor: (t) => alpha(t.palette.error.main, 0.06) } : undefined}
                      >
                        {report.columns.map((c) => (
                          <TableCell key={c}>
                            <CellView cell={r.cells[c]} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {report.truncated ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Raport ucięty — zbyt wiele zleceń w okresie; zawęź zakres w konfiguracji.
              </Alert>
            ) : null}
            <Typography variant="body2" sx={{ mt: 1 }} data-testid="client-report-plan-note">
              {report.planNote}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info" sx={{ mb: 1 }}>
          Raport czeka na przygotowanie — kliknij „Przygotuj”.
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 1 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Plik i wiadomość
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
            {open ? (
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => void act(() => prepareReport(report.id), 'Raport przygotowany')}
                data-testid="client-report-prepare"
              >
                {prepared ? 'Przygotuj ponownie' : 'Przygotuj'}
              </Button>
            ) : null}
            {prepared ? (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    void downloadReportFile(report.id, 'xlsx').catch((e) => notify(toErrorMessage(e), { type: 'error' }))
                  }
                  data-testid="client-report-download-xlsx"
                >
                  Pobierz Excel
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    void downloadReportFile(report.id, 'csv').catch((e) => notify(toErrorMessage(e), { type: 'error' }))
                  }
                  data-testid="client-report-download-csv"
                >
                  Pobierz CSV
                </Button>
              </>
            ) : null}
          </Stack>
          {report.draft ? (
            <>
              <TextField
                fullWidth
                size="small"
                label="Do"
                value={recipients}
                disabled={!open}
                onChange={(e) => setRecipients(e.target.value)}
                helperText="adresy z konfiguracji — możesz je zmienić przed otwarciem wiadomości"
                slotProps={{ htmlInput: { 'data-testid': 'client-report-recipients' } }}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {report.draft.subject}
              </Typography>
              <Box
                component="pre"
                data-testid="client-report-draft"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, m: 0, mt: 0.5 }}
              >
                {report.draft.body}
              </Box>
              <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" onClick={() => void copy()} data-testid="client-report-copy">
                  Kopiuj treść
                </Button>
                {mail ? (
                  <Button
                    size="small"
                    variant="outlined"
                    href={mail.href}
                    onClick={() => {
                      if (!mail.bodyIncluded) void copy();
                    }}
                    data-testid="client-report-mailto"
                  >
                    Otwórz w poczcie
                  </Button>
                ) : null}
              </Stack>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Wiadomość wysyłasz samodzielnie ze swojej poczty — dołącz pobrany plik raportu. Potem oznacz raport jako
                wysłany.
              </Typography>
            </>
          ) : null}
          {open && prepared ? (
            <Box sx={{ mt: 1 }}>
              {missing ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={withMissing}
                      onChange={(e) => setWithMissing(e.target.checked)}
                      data-testid="client-report-with-missing"
                    />
                  }
                  label={`Wysłałem raport z oznaczonymi brakami (${report.missingCount})`}
                />
              ) : null}
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={busy || (missing && !withMissing)}
                  onClick={() =>
                    void act(
                      () => markSent(report.id, { recipients, withMissing: missing ? withMissing : undefined }),
                      'Raport oznaczony jako wysłany',
                    )
                  }
                  data-testid="cs-raport-akceptuj-wyslij"
                >
                  {missing ? 'Wysłane z oznaczonymi brakami' : 'Akceptuję — wysłane ręcznie'}
                </Button>
                <Button size="small" onClick={() => setHolding(true)} disabled={busy} data-testid="client-report-hold">
                  Wstrzymaj
                </Button>
              </Stack>
            </Box>
          ) : null}
          {open && !prepared ? (
            <Button size="small" sx={{ mt: 1 }} onClick={() => setHolding(true)} data-testid="client-report-hold">
              Wstrzymaj
            </Button>
          ) : null}
          {report.status === 'on_hold' ? (
            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
              disabled={busy}
              onClick={() => void act(() => resumeReport(report.id), 'Raport wznowiony')}
              data-testid="client-report-resume"
            >
              Wznów
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2">Historia raportu</Typography>
          <Box data-testid="client-report-history">
            {report.events.map((e) => (
              <Typography key={e.id} variant="body2" sx={{ py: 0.25 }}>
                {dateTimeNoSeconds(e.at)} · {e.byName ?? 'automat'} · {e.kindLabel}
                {e.note ? ` — ${e.note}` : ''}
              </Typography>
            ))}
          </Box>
        </CardContent>
      </Card>

      <HoldDialog
        open={holding}
        onClose={() => setHolding(false)}
        onConfirm={async (reason) => {
          setHolding(false);
          await act(() => holdReport(report.id, reason), 'Raport wstrzymany');
        }}
      />
    </Box>
  );
};

/**
 * Kolejka raportów dla klientów (prace 26.10.2026, pakiet P2, S-26.10-05,
 * przełącznik `clientReportQueue`; makieta p. Mariusza, ekrany 7–8;
 * BR-CS-11, ścieżki CS-02 i CS-05): według terminu, ze stanem, „po
 * terminie” i filtrem po kliencie; wiersz otwiera podgląd. Kolejność
 * i stany liczy API.
 */
export const ClientReportQueueScreen = () => {
  const on = useFeatureState(FEATURE_CLIENT_REPORT_QUEUE);
  const [filter, setFilter] = useState<QueueFilter>({ status: 'open' });
  const [queue, setQueue] = useState<ReportQueue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (on !== true) return;
    let alive = true;
    const t = setTimeout(() => {
      fetchQueue(filter)
        .then((q) => {
          if (!alive) return;
          setQueue(q);
          setError(null);
        })
        .catch((e) => {
          if (alive) setError(toErrorMessage(e));
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [on, filter, tick]);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;

  return (
    <Box sx={{ p: 1 }} data-testid="client-reports">
      <Title title="Raporty dla klientów" />
      {selected !== null ? (
        <ReportPreview id={selected} onBack={() => setSelected(null)} onChanged={() => setTick((n) => n + 1)} />
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Raporty powstają z harmonogramu konfiguracji albo przyciskiem „Przygotuj teraz” w konfiguracji raportów.
              CS sprawdza dane, pobiera plik, wysyła wiadomość ze swojej poczty i oznacza raport jako wysłany.
            </Typography>
            {queue ? (
              <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }} data-testid="client-reports-counts">
                <Chip size="small" label={`czeka na CS: ${queue.counts.open}`} />
                <Chip size="small" color="error" variant="outlined" label={`niekompletne: ${queue.counts.incomplete}`} />
                <Chip size="small" color="warning" variant="outlined" label={`do akceptacji: ${queue.counts.toAccept}`} />
                <Chip size="small" color="error" label={`po terminie: ${queue.counts.overdue}`} />
              </Stack>
            ) : null}
            <Box
              sx={{ display: 'grid', gap: 1, mb: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))' }}
            >
              <TextField
                size="small"
                label="Klient"
                value={filter.client ?? ''}
                onChange={(e) => setFilter((f) => ({ ...f, client: e.target.value }))}
                slotProps={{ htmlInput: { 'data-testid': 'client-reports-client' } }}
              />
              <TextField
                select
                size="small"
                label="Stan"
                value={filter.status ?? ''}
                onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
                slotProps={{ htmlInput: { 'data-testid': 'client-reports-status' } }}
              >
                <MenuItem value="open">czeka na CS</MenuItem>
                <MenuItem value="">wszystkie</MenuItem>
                {(queue?.statuses ?? []).map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!filter.overdue}
                    onChange={(e) => setFilter((f) => ({ ...f, overdue: e.target.checked }))}
                  />
                }
                label="Tylko po terminie"
              />
            </Box>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {queue && queue.items.length === 0 ? (
              <Typography variant="body2" color="text.secondary" data-testid="client-reports-empty">
                Brak raportów w kolejce.
              </Typography>
            ) : null}
            {queue && queue.items.length > 0 ? (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Termin</TableCell>
                      <TableCell>Klient</TableCell>
                      <TableCell>Raport</TableCell>
                      <TableCell>Lokalizacja</TableCell>
                      <TableCell>Stan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queue.items.map((r) => (
                      <TableRow
                        key={r.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelected(r.id)}
                        data-testid={`client-report-item-${r.id}`}
                        data-status={r.status}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTimeNoSeconds(r.dueAt)}</TableCell>
                        <TableCell>{r.clientName}</TableCell>
                        <TableCell>
                          {r.reportName}
                          <Typography variant="caption" color="text.secondary" component="div">
                            {r.periodLabel} · {r.triggerLabel}
                          </Typography>
                        </TableCell>
                        <TableCell>{r.locationsLabel}</TableCell>
                        <TableCell>
                          <Chip size="small" color={STATUS_COLOR[r.status] ?? 'default'} label={r.statusLabel} />
                          {r.overdue ? <Chip size="small" color="error" label="po terminie" sx={{ ml: 0.5 }} /> : null}
                          {r.missingCount ? (
                            <Chip size="small" variant="outlined" color="error" label={`braki: ${r.missingCount}`} sx={{ ml: 0.5 }} />
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : null}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
